import React from 'react'
import { z } from 'zod'

import { Button } from '../../catalyst/button'
import { Heading } from '../../catalyst/heading'
import { Strong, Text, TextLink } from '../../catalyst/text'
import { Logo } from '../../components/Logo'
import { schemas, TextField, useNativeFormSubmit, useValidatedForm } from '../../components/forms'

interface ForgotPasswordProps {
  actionUrl: string
  signInUrl: string
  signUpUrl: string
  authenticity_token: string
  flash?: Record<string, string>
}

// Nested to mirror the params hash Rails receives; see `submitName` in
// components/forms/fields.
const resetSchema = z.object({ user: z.object({ email: schemas.email() }) })

type ResetValues = z.infer<typeof resetSchema>

export function ForgotPassword({
  actionUrl,
  authenticity_token,
  signUpUrl,
  flash,
}: ForgotPasswordProps) {
  const form = useValidatedForm<ResetValues>(resetSchema, {
    defaultValues: { user: { email: '' } },
  })
  const formProps = useNativeFormSubmit(form)

  return (
    <div className="w-full max-w-sm">
      <form
        action={actionUrl}
        method="POST"
        className="grid w-full grid-cols-1 gap-8"
        {...formProps}
      >
        <input type="hidden" name="authenticity_token" value={authenticity_token} />

        <div>
          <a href="/" aria-label="Home">
            <Logo className="mx-auto h-12 w-auto text-zinc-950 dark:text-white" />
          </a>
          <Heading className="mt-6 text-center">Reset your password</Heading>
        </div>

        <Text className="text-center">
          Enter your email and we’ll send you a link to reset your password.
        </Text>

        {flash?.alert && (
          <div
            role="alert"
            className="rounded-md bg-red-50 p-4 text-sm text-red-700 dark:bg-red-900/50 dark:text-red-200"
          >
            {flash.alert}
          </div>
        )}
        {flash?.notice && (
          <div className="rounded-md bg-green-50 p-4 text-sm text-green-700 dark:bg-green-900/50 dark:text-green-200">
            {flash.notice}
          </div>
        )}

        <TextField
          form={form}
          name="user.email"
          submitName="user[email]"
          label="Email"
          type="email"
          autoComplete="email"
          autoFocus
        />

        <Button type="submit" className="w-full">
          Reset password
        </Button>

        <Text className="text-center">
          Don't have an account? Your lodge secretary or exalted ruler can invite you, or you can{' '}
          <TextLink href={signUpUrl}>
            <Strong>apply to join a lodge</Strong>
          </TextLink>{' '}
          yourself.
        </Text>
      </form>
    </div>
  )
}
